import { StateCreator } from 'zustand';
import type { TeamName, RoundNumber } from '@business-game/shared';

export interface GameState {
  // Game state
  currentRound: RoundNumber;
  maxRounds: number;
  pointsPerRound: number;
  baseScore: number;

  // Team info
  teamName: TeamName | null;
  teamId: number | null;

  // Session info
  sessionCode: string | null;

  // Selected criterias for current round
  selectedCriterias: Record<number, number>; // criteriaId → points

  // Actions
  setTeam: (name: TeamName, id: number) => void;
  setSession: (code: string) => void;
  setGameConfig: (config: { baseScore: number; maxPointsPerRound: number; numberOfRounds: number }) => void;
  selectCriteria: (criteriaId: number, points: number) => void;
  clearSelection: () => void;
  nextRound: () => void;
  resetGame: () => void;

  // Computed
  getTotalPoints: () => number;
  getRemainingPoints: () => number;
}

export const createGameSlice: StateCreator<GameState> = (set, get) => ({
  // Initial state - load from localStorage if available
  currentRound: (() => {
    const stored = localStorage.getItem('current_round');
    return stored ? (parseInt(stored, 10) as RoundNumber) : 1;
  })(),
  maxRounds: 10,
  pointsPerRound: 3,
  baseScore: 20,
  teamName: (localStorage.getItem('team_name') as TeamName | null) || null,
  teamId: (() => {
    const stored = localStorage.getItem('team_id');
    return stored ? parseInt(stored, 10) : null;
  })(),
  sessionCode: localStorage.getItem('session_code'),
  selectedCriterias: {},

  // Actions
  setTeam: (name, id) => {
    set({ teamName: name, teamId: id });
    // Save to localStorage for persistence
    localStorage.setItem('team_name', name);
    localStorage.setItem('team_id', String(id));
  },

  setSession: (code) => {
    const oldCode = localStorage.getItem('session_code');
    if (oldCode && oldCode !== code) {
      // Switching to a different session: clear game state from previous session
      localStorage.removeItem('team_name');
      localStorage.removeItem('team_id');
      localStorage.removeItem('current_round');
      localStorage.removeItem('last_verbatim');
      set({ sessionCode: code, teamName: null, teamId: null, currentRound: 1 });
    } else {
      set({ sessionCode: code });
    }
    localStorage.setItem('session_code', code);
  },

  setGameConfig: (config) => {
    set({
      baseScore: config.baseScore,
      pointsPerRound: config.maxPointsPerRound,
      maxRounds: config.numberOfRounds,
    });
  },

  selectCriteria: (criteriaId, points) => {
    set((state) => {
      const newSelection = { ...state.selectedCriterias };

      if (points === 0) {
        delete newSelection[criteriaId];
      } else {
        newSelection[criteriaId] = points;
      }

      return { selectedCriterias: newSelection };
    });
  },

  clearSelection: () => {
    set({ selectedCriterias: {} });
  },

  nextRound: () => {
    set((state) => {
      const nextRound = Math.min(state.currentRound + 1, state.maxRounds) as RoundNumber;
      localStorage.setItem('current_round', String(nextRound));
      return {
        currentRound: nextRound,
        selectedCriterias: {},
      };
    });
  },

  resetGame: () => {
    set({
      currentRound: 1,
      selectedCriterias: {},
      teamName: null,
      teamId: null,
      sessionCode: null,
    });
    localStorage.setItem('current_round', '1');
    localStorage.removeItem('team_name');
    localStorage.removeItem('team_id');
    localStorage.removeItem('session_code');
    localStorage.removeItem('last_verbatim');
  },

  // Computed
  getTotalPoints: () => {
    const { selectedCriterias } = get();
    return Object.values(selectedCriterias).reduce((sum, points) => sum + points, 0);
  },

  getRemainingPoints: () => {
    const { pointsPerRound } = get();
    return pointsPerRound - get().getTotalPoints();
  },
});
